import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigBaseFilter, ConfigService, ListLayout, ListLayoutMultipleComponent, ListSortOption, SimpleConfigBaseFilter, SkeletonLoaderLayout } from 'gung-list';
import { BehaviorSubject, first, map, Observable, switchMap } from 'rxjs';
import { TopProductsSummaryComponent } from '../../components/top-products/top-products-summary/top-products-summary.component';
import { OrderStatistic } from '../../models/order-statistics';
import { OrderStatisticsService } from '../order-statistics/order-statistics.service';

@Injectable({
  providedIn: 'root'
})
export class TopProductsListConfigService implements ConfigService<OrderStatistic> {
  protected orderStatisticsService = inject(OrderStatisticsService);
  protected http = inject(HttpClient);
  
  skeletonLoaderLayout: SkeletonLoaderLayout = 'table';
  topFilter = true;
  seasons = {};
  seasonToShow = {};
  public seasonList = [];
  orderStatisticsCache: OrderStatistic[];
  seasonsAllowed = [];
  // Shows from/to date fields above the top products list.
  // Off by default because johnhatter, five-seasons, theorema and elodie override getItems() and would ignore the dates.
  showDateRange = false;
  dateRange = new BehaviorSubject<{ from: string; to: string }>(this.getDefaultDateRange());

  getFilters(): ConfigBaseFilter<OrderStatistic>[] {
    return [
      new OrderTypeDescriptionFilter(),
      new OrderSeasonCodeFilter(),
      new CustomerNameFilter(),
      new ProductPrimaryDimensionNameFilter(),
      new ProductCategoryDescriptionFilter(),
      new CustomerCountryCodeFilter()
    ];
  }

  getSortOptions(): ListSortOption<OrderStatistic>[] {
    return [];
  }

  getBatchSizes(): number[] {
    return [-1];
  }

  getLayouts(): ListLayout<OrderStatistic>[] {
    return [
      {
        getIconClass: () => 'fas fa-th',
        getListItemComponent: () => TopProductsSummaryComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductGrid'
      }
    ];
  }

  formatDate(date) {
    const d = new Date(date);
    let month = (d.getMonth() + 1).toString();
    let day = d.getDate().toString();
    const year = d.getFullYear();
    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }
    return [year, month, day].join('-');
  }

  getDefaultDateRange(): { from: string; to: string } {
    const from = new Date();
    from.setFullYear(from.getFullYear() - 1);
    return { from: this.formatDate(from), to: this.formatDate(new Date()) };
  }

  getItems(): Observable<OrderStatistic[]> {
    if (!this.showDateRange) {
      return this.orderStatisticsService.getOrderStatistics(this.getDefaultDateRange().from).pipe(first());
    }
    return this.dateRange.pipe(
      switchMap(({ from, to }) =>
        this.orderStatisticsService.getOrderStatistics(from).pipe(
          first(),
          // order-statistics-nostream only supports minDate, so the upper bound is applied here
          map(statistics => statistics.filter(statistic => !to || this.formatDate(statistic.orderCreationDate) <= to))
        )
      )
    );
  }

  getItemId(item: OrderStatistic) {
    return item.id;
  }

  getSearchTerms(item: OrderStatistic) {
    return [item.productId, item.productName];
  }

  getNothingFoundTranslateTag() {
    return 'NOTHING_FOUND_CONTACT_GUNG';
  }
}

export class ProductPrimaryDimensionNameFilter extends SimpleConfigBaseFilter<OrderStatistic> {
  getName(): string {
    return 'Product Color';
  }

  getOptionIds(item: OrderStatistic): string[] {
    return [item.productPrimaryDimensionName];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class ProductCategoryDescriptionFilter extends SimpleConfigBaseFilter<OrderStatistic> {
  getName(): string {
    return 'Product Category';
  }

  getOptionIds(item: OrderStatistic): string[] {
    return [item.productCategoryDescription];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class OrderSeasonCodeFilter extends SimpleConfigBaseFilter<OrderStatistic> {
  getName(): string {
    return 'SEASON';
  }

  getOptionIds(item: OrderStatistic): string[] {
    return [item.orderSeasonCode];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class CustomerNameFilter extends SimpleConfigBaseFilter<OrderStatistic> {
  getName(): string {
    return 'Customer';
  }

  getOptionIds(item: OrderStatistic): string[] {
    return [item.customerId + ' - ' + item.customerName];
  }

  getOptionName(key: string): string {
    return key;
  }
}
export class OrderTypeDescriptionFilter extends SimpleConfigBaseFilter<OrderStatistic> {
  getName(): string {
    return 'ORDER_TYPE';
  }

  getOptionIds(item: OrderStatistic): string[] {
    return [item.orderTypeDescription];
  }

  getOptionName(key: string): string {
    return key;
  }
}

export class CustomerCountryCodeFilter extends SimpleConfigBaseFilter<OrderStatistic> {
  getName(): string {
    return 'CUSTOMER_COUNTRY';
  }

  getOptionIds(item: OrderStatistic): string[] {
    return item.customerCountryCode ? [item.customerCountryCode] : [];
  }

  getOptionName(key: string): string {
    return key;
  }
}
